import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Toaster } from './components/ui/sonner';
import { ErrorBoundary } from './components/ErrorBoundary';

// Auth Pages
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import LedgerPage from './pages/LedgerPage';
import DealSecuritySchedulePage from './pages/admin/DealSecuritySchedulePage';
import PublicDealPage from './pages/PublicDealPage';

// Dashboard Layout
import DashboardLayout from './components/layout/DashboardLayout';

// Dashboard Pages
import DashboardPage from './pages/DashboardPage';
import InvestmentPortfolioPage from './pages/InvestmentPortfolioPage';
import StatementsPage from './pages/StatementsPage';
import AdminStatementsPage from './pages/admin/AdminStatementsPage';
import DistributionsPage from './pages/DistributionsPage';
import DocumentVaultPage from './pages/DocumentVaultPage';
import KYCPage from './pages/KYCPage';
import AdminKYCReviewPage from './pages/AdminKYCReviewPage';
import TaxCentrePage from './pages/TaxCentrePage';
import NoticesPage from './pages/NoticesPage';
import AdminNoticesPage from './pages/AdminNoticesPage';
import DealRoomPage from './pages/DealRoomPage';
import DealDetailPage from './pages/DealDetailPage';
import AdminDealsPage from './pages/AdminDealsPage';
import AdminDealRoomPage from './pages/admin/AdminDealRoomPage';
import ReportsPage from './pages/ReportsPage';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import SupportPage from './pages/SupportPage';

// Admin Layout and Pages
import AdminLayout from './components/layout/AdminLayout';
import AdminDashboardHome from './pages/admin/AdminDashboardHome';
import AdminInvestorsPage from './pages/admin/AdminInvestorsPage';
import AdminSupportPage from './pages/admin/AdminSupportPage';
import AdminClientsPage from './pages/admin/AdminClientsPage';
import AdminClientLoanLedgerPage from './pages/admin/AdminClientLoanLedgerPage';
import AdminDataImportPage from './pages/admin/AdminDataImportPage';
import AdminInvestorPricingPage from './pages/admin/AdminInvestorPricingPage';
import AdminLoanbookShadowPage from './pages/admin/AdminLoanbookShadowPage';
import AdminFundRequestsPage from './pages/admin/AdminFundRequestsPage';
import AdminMonthlySubmissionsPage from './pages/admin/AdminMonthlySubmissionsPage';
import AdminLMSPage from './pages/admin/AdminLMSPage';
import AdminInvestecPage from './pages/admin/AdminInvestecPage';
import AdminInternalTransfersPage from './pages/admin/AdminInternalTransfersPage';
import AdminWithdrawalRequestsPage from './pages/admin/AdminWithdrawalRequestsPage';
import AdminTransactionsPage from './pages/admin/AdminTransactionsPage';
import AdminAuditTrailPage from './pages/admin/AdminAuditTrailPage';
import AdminManualsPage from './pages/admin/AdminManualsPage';
import AdminTemplatesPage from './pages/admin/AdminTemplatesPage';
import AdminMediaPage from './pages/admin/AdminMediaPage';
import AdminRiskTranchesPage from './pages/admin/AdminRiskTranchesPage';
import AdminOCRPage from './pages/admin/AdminOCRPage';
import AdminDistributionsPage from './pages/admin/AdminDistributionsPage';
import AdminLedgerPage from './pages/admin/AdminLedgerPage';
import AdminTaxCertificatesPage from './pages/admin/AdminTaxCertificatesPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import AdminCompanySettingsPage from './pages/admin/AdminCompanySettingsPage';
import AdminUserManagementPage from './pages/admin/AdminUserManagementPage';
import AdminSectorsPage from './pages/admin/AdminSectorsPage';
import AdminDocumentsPage from './pages/admin/AdminDocumentsPage';
import AdminApiKeysPage from './pages/admin/AdminApiKeysPage';

// Investor Pages
import WithdrawalRequestsPage from './pages/investor/WithdrawalRequestsPage';
import ContributionRequestsPage from './pages/investor/ContributionRequestsPage';
import MarketplacePage from './pages/investor/MarketplacePage';

// Compliance / IR staff pages
import CompliancePage from './pages/CompliancePage';
import IRDashboardPage from './pages/IRDashboardPage';
import AdminContributionRequestsPage from './pages/AdminContributionRequestsPage';
import AdminMarketplacePage from './pages/AdminMarketplacePage';

// Client Layout and Pages
import ClientLayout from './components/layout/ClientLayout';
import ClientDashboardPage from './pages/client/ClientDashboardPage';
import ClientLoansPage from './pages/client/ClientLoansPage';
import ClientFundRequestsPage from './pages/client/ClientFundRequestsPage';
import ClientMonthlySubmissionsPage from './pages/client/ClientMonthlySubmissionsPage';
import ClientSettingsPage from './pages/client/ClientSettingsPage';
import ClientProfilePage from './pages/client/ClientProfilePage';
import ClientTransactionsPage from './pages/client/ClientTransactionsPage';
import ClientStatementsPage from './pages/client/ClientStatementsPage';
import ClientSupportPage from './pages/client/ClientSupportPage';
import ClientKYCPage from './pages/client/ClientKYCPage';

// Placeholder pages - will be implemented in subsequent prompts
const PlaceholderPage = ({ title }) => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <div className="text-center">
      <h1 className="text-2xl font-bold text-[#212529] font-['Manrope'] mb-2">{title}</h1>
      <p className="text-[#6C757D]">Coming soon in the next phase</p>
    </div>
  </div>
);

// Smart redirect based on user role
const RoleBasedRedirect = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (user.role === 'client') {
    return <Navigate to="/client" replace />;
  }
  
  if (user.role === 'admin' || user.role === 'analyst' || user.role === 'operations') {
    return <Navigate to="/admin" replace />;
  }

  if (user.role === 'compliance') {
    return <Navigate to="/compliance" replace />;
  }

  if (user.role === 'investor_relations') {
    return <Navigate to="/ir" replace />;
  }
  
  // Default to investor dashboard
  return <Navigate to="/dashboard" replace />;
};

// Guards the "Administration" cascade (API keys, audit trail, company/system
// settings, templates, user management, ...) within the admin portal —
// analysts get everything else under AdminLayout but not this. The backend
// already 403s these for non-admins; this just avoids a broken page/blind
// API-error screen and sends them back to the admin dashboard instead.
const AdminOnlySection = ({ children }) => {
  const { isAdmin } = useAuth();
  if (!isAdmin) {
    return <Navigate to="/admin" replace />;
  }
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ErrorBoundary>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ForgotPasswordPage />} />
          {/* Public read-only deal share view — no auth required at the
              route level; the page itself handles PIN / auth prompts. */}
          <Route path="/deal/:token" element={<PublicDealPage />} />

          {/* Investor Protected Routes */}
          <Route
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/portfolio" element={<InvestmentPortfolioPage />} />
            <Route path="/statements" element={<StatementsPage />} />
            <Route path="/distributions" element={<DistributionsPage />} />
            <Route path="/documents" element={<DocumentVaultPage />} />
            <Route path="/kyc" element={<KYCPage />} />
            <Route path="/tax" element={<TaxCentrePage />} />
            <Route path="/notices" element={<NoticesPage />} />
            <Route path="/deals" element={<DealRoomPage />} />
            <Route path="/deals/:dealId" element={<DealDetailPage />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/ledger" element={<LedgerPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/withdrawals" element={<WithdrawalRequestsPage />} />
            <Route path="/additions" element={<ContributionRequestsPage />} />
            <Route path="/marketplace" element={<MarketplacePage />} />
          </Route>

          {/* Compliance / Investor Relations staff pages — reuse the
              investor DashboardLayout so we get header/sidebar; each
              page enforces role via API. */}
          <Route
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/compliance" element={<CompliancePage />} />
            <Route path="/ir" element={<IRDashboardPage />} />
          </Route>

          {/* Client/Borrower Portal Routes */}
          <Route element={<ClientLayout />}>
            <Route path="/client" element={<ClientDashboardPage />} />
            <Route path="/client/loans" element={<ClientLoansPage />} />
            <Route path="/client/transactions" element={<ClientTransactionsPage />} />
            <Route path="/client/statements" element={<ClientStatementsPage />} />
            <Route path="/client/fund-requests" element={<ClientFundRequestsPage />} />
            <Route path="/client/monthly-submissions" element={<ClientMonthlySubmissionsPage />} />
            <Route path="/client/kyc" element={<ClientKYCPage />} />
            <Route path="/client/profile" element={<ClientProfilePage />} />
            <Route path="/client/settings" element={<ClientSettingsPage />} />
            <Route path="/client/support" element={<ClientSupportPage />} />
          </Route>
            
          {/* Admin Routes with Admin Layout */}
          <Route element={<AdminLayout />}>
            <Route path="/admin" element={<AdminDashboardHome />} />
            <Route path="/admin/investors" element={<AdminInvestorsPage />} />
            <Route path="/admin/clients" element={<AdminClientsPage />} />
            <Route path="/admin/clients/:clientId/loan-ledger" element={<AdminClientLoanLedgerPage />} />
            <Route path="/admin/loans" element={<AdminLMSPage />} />
            <Route path="/admin/fund-requests" element={<AdminFundRequestsPage />} />
            <Route path="/admin/monthly-submissions" element={<AdminMonthlySubmissionsPage />} />
            <Route path="/admin/import" element={<AdminDataImportPage />} />
            <Route path="/admin/investor-pricing" element={<AdminInvestorPricingPage />} />
            <Route path="/admin/loanbook-engine/shadow" element={<AdminLoanbookShadowPage />} />
            <Route path="/admin/statements" element={<AdminStatementsPage />} />
            <Route path="/admin/manuals" element={<AdminManualsPage />} />
            <Route path="/admin/investec" element={<AdminInvestecPage />} />
            <Route path="/admin/internal-transfers" element={<AdminInternalTransfersPage />} />
            <Route path="/admin/withdrawal-requests" element={<AdminWithdrawalRequestsPage />} />
            <Route path="/admin/contribution-requests" element={<AdminContributionRequestsPage />} />
            <Route path="/admin/kyc-compliance" element={<CompliancePage />} />
            <Route path="/admin/portfolio-metrics" element={<IRDashboardPage />} />
            <Route path="/admin/marketplace" element={<AdminMarketplacePage />} />
            <Route path="/admin/transactions" element={<AdminTransactionsPage />} />
            <Route path="/admin/audit-trail" element={<AdminOnlySection><AdminAuditTrailPage /></AdminOnlySection>} />
            <Route path="/admin/templates" element={<AdminOnlySection><AdminTemplatesPage /></AdminOnlySection>} />
            <Route path="/admin/media" element={<AdminMediaPage />} />
            <Route path="/admin/distributions" element={<AdminDistributionsPage />} />
            <Route path="/admin/ledger" element={<AdminLedgerPage />} />
            <Route path="/admin/documents" element={<AdminDocumentsPage />} />
            <Route path="/admin/notices" element={<AdminNoticesPage />} />
            <Route path="/admin/deals" element={<AdminDealsPage />} />
            <Route path="/admin/deal-room" element={<AdminDealRoomPage />} />
            <Route path="/admin/deals/:dealId/security" element={<DealSecuritySchedulePage />} />
            <Route path="/admin/risk-tranches" element={<AdminRiskTranchesPage />} />
            <Route path="/admin/ocr" element={<AdminOnlySection><AdminOCRPage /></AdminOnlySection>} />
            <Route path="/admin/kyc-review" element={<AdminKYCReviewPage />} />
            <Route path="/admin/tax" element={<AdminTaxCertificatesPage />} />
            <Route path="/admin/sectors" element={<AdminSectorsPage />} />
            <Route path="/admin/support" element={<AdminOnlySection><AdminSupportPage /></AdminOnlySection>} />
            <Route path="/admin/settings" element={<AdminOnlySection><AdminSettingsPage /></AdminOnlySection>} />
            <Route path="/admin/company-settings" element={<AdminOnlySection><AdminCompanySettingsPage /></AdminOnlySection>} />
            <Route path="/admin/api-keys" element={<AdminOnlySection><AdminApiKeysPage /></AdminOnlySection>} />
            <Route path="/admin/user-management" element={<AdminOnlySection><AdminUserManagementPage /></AdminOnlySection>} />
            <Route path="/admin/users" element={<AdminInvestorsPage />} />
            <Route path="/admin/invites" element={<PlaceholderPage title="Investor Invitations" />} />
          </Route>

          {/* Default redirect based on role */}
          <Route path="/" element={<RoleBasedRedirect />} />
          <Route path="*" element={<RoleBasedRedirect />} />
        </Routes>
        </ErrorBoundary>
      </BrowserRouter>
      <Toaster position="top-right" />
    </AuthProvider>
  );
}

export default App;
